import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableComponent from 'components/table-component/Table';
import toast from 'react-hot-toast';
import { cloneDeep } from 'lodash';
import plusIcon from 'assets/images/icons/plus.svg';
import CompanyFieldFormModal from './CompanyFieldFormModal';
import CompanyFieldListHeaders from './CompanyFieldListHeaders';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import Error from 'components/Error';
import FormButton from 'components/form-components/FormButton';
import { isPermissionAvailable } from 'config/permissionUtils';
import { permissionKeys } from 'config/config';
import { CallAPI } from 'actions/General';
import { CompanyFieldHeaderList } from './utils';
import ClientPagination from './ClientPagination';

const CompanyField = () => {
  const { t } = useTranslation();

  const [companyFields, setCompanyFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inputFilters, setInputFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [APIError, setAPIError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);

  // Handle input filter changes
  const handleFilterChange = (name, value) => {
    setInputFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDebouncedSearch = (name, value) => {
    setAppliedFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Add button click
  const handleAddCompanyField = () => {
    setAppliedFilters({});
    setInputFilters({});
    setModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setSelectedRecordId(null);
    setModalVisible(false);
  };

  const handleDeleteRecord = async () => {
    const fieldCheckResult = await CallAPI(
      'GET_COMPANY_FIELD_LINKED_FORMS',
      { ids: [selectedRecordId] },
      null,
      setDeleteLoading,
      setAPIError,
      null,
    );
    if (fieldCheckResult.status) {
      if (!fieldCheckResult.data.length) {
        const result = await CallAPI(
          'DELETE_COMPANY_FIELDS',
          null,
          selectedRecordId,
          setDeleteLoading,
          setAPIError,
          null,
        );
        if (result.status) {
          toast.success(t('alert_success_company_field_deleted'));
          fetchCompanyFieldList();
        }
        setDeleteConfirmationModal(false);
        setSelectedRecordId(null);
      } else {
        toast.error(t('alert_success_company_field_used_in_form'));
        setDeleteConfirmationModal(false);
        setSelectedRecordId(null);
      }
    }
  };

  const actionsCallBack = async (action, data) => {
    if (action === 'add') {
      handleAddCompanyField();
    } else if (action === 'edit') {
      setSelectedRecordId(data.id);
      setModalVisible(true);
    } else if (action === 'delete') {
      setSelectedRecordId(data.id);
      setDeleteConfirmationModal(true);
    }
  };

  const fetchCompanyFieldList = async () => {
    setSelectedRecordId(null);
    const result = await CallAPI(
      'GET_ALL_COMPANY_FIELDS',
      { searchText: appliedFilters?.search ?? '', withDeleted: true },
      null,
      setLoading,
      setAPIError,
      null,
    );
    if (result.status) {
      const transformCompanyFields = result.data.map((item) => ({
        ...item,
        dataSourceName: item.dataSource?.schemaName ?? '',
        dataSourceColumnName:
          item.dataSource?.dataSourceSchema?.find(
            (schema) => schema.columnId === item.dataSourceColumn,
          )?.columnName ?? '',
      }));
      setCompanyFields(transformCompanyFields);
    }
  };

  const onRowsSelection = (name, value) => {
    let tempSelection = [];
    const mappedObject = (obj) => ({ id: obj._id, isDeleted: obj.isDeleted });

    if (name === 'all') {
      if (value) tempSelection = companyFields.map((s) => mappedObject(s));
      else tempSelection = [];
    } else {
      tempSelection = cloneDeep(selectedRows);

      if (value) {
        const companyField = companyFields.find((cf) => cf._id === name);

        if (companyField) tempSelection.push(mappedObject(companyField));
      } else tempSelection = tempSelection.filter((s) => s.id !== name);
    }

    setSelectedRows(tempSelection);
  };

  // Fetch on load or filter change
  useEffect(() => {
    fetchCompanyFieldList();
  }, [appliedFilters]);

  return (
    <div>
      {APIError && <Error msg={APIError} />}

      <CompanyFieldListHeaders
        filteringData={inputFilters}
        setFilteringData={handleFilterChange}
        debounceFunc={handleDebouncedSearch}
        selectedRows={selectedRows}
        actionsCallBack={actionsCallBack}
      />

      <TableComponent
        header={CompanyFieldHeaderList}
        data={paginatedData}
        props={{
          translateValueFunc: (val) => t(val),
          actionsCallBack: fetchCompanyFieldList,
          actionOnclick: actionsCallBack,
          type: 'company_fields',
        }}
        totalRecords={0}
        loader={loading}
        filterEnabled={false}
        isLoading={loading}
        isFieldSelectionEnabled={false}
        isPaginationEnabled={false}
        isActionsEnabled={false}
        isSelectionEnabled={true}
        selectionKey={'_id'}
        onSelection={onRowsSelection}
        selectedRows={selectedRows}
        noDataComponent={
          <div className='lh-1 w-100 d-flex flex-column gap-2 justify-content-center align-items-center'>
            <span className='theme-text-black-1 theme-size-1_3'>
              {t('text_you_dont_have_any_form_company_field')}
            </span>
            {isPermissionAvailable(permissionKeys.field_work_response_set_create) && (
              <>
                <span>{t('text_add_company_field_to_get_started')}</span>
                <FormButton
                  text='button_add_new'
                  variant='green-1'
                  onClick={handleAddCompanyField}
                  icon={plusIcon}
                  iconClasses='svg-white'
                />
              </>
            )}
          </div>
        }
      />

      {companyFields.length > 0 && (
        <div className='p-3'>
          <ClientPagination
            data={companyFields}
            onChange={({ paginatedDataList }) => {
              setPaginatedData(paginatedDataList);
            }}
          />
        </div>
      )}

      {/* Modal for Add/Edit */}
      {modalVisible && (
        <CompanyFieldFormModal
          handleClose={handleCloseEditModal}
          refreshList={fetchCompanyFieldList}
          selectedRecordId={selectedRecordId}
        />
      )}

      <ConfirmationModal
        show={deleteConfirmationModal}
        title={t('alert_company_field_deletion_title')}
        message={t('alert_company_field_deletion_message')}
        actionList={[
          {
            color: 'black-1',
            text: t('button_cancel'),
            onClick: () => setDeleteConfirmationModal(false),
          },
          {
            color: 'red-1',
            text: t('button_delete'),
            onClick: () => handleDeleteRecord(),
            loading: deleteLoading,
          },
        ]}
      />
    </div>
  );
};

export default CompanyField;
